import React, { useState } from 'react';
import { useDragLayer, useDrop } from 'react-dnd';
import { createPortal } from 'react-dom';
import Tooltip from '../../components/Tooltip';
import type QuillEditor from '../QuillEditor';
import type Uploader from '../Uploader';
import type { ThumbnailDragItem } from './CarouselThumbnail';
import { CAROUSEL_ITEM_DRAG_TYPE } from './CarouselThumbnail';
import * as styles from './CarouselThumbnailDragPreview.module.scss';
import CarouselThumbnailImage from './CarouselThumbnailImage';
import { isUploadedCarouselItem } from './types';

enum DragState {
  Remove,
  MoveToEditor,
  None,
}

const TOOLTIP_LABELS: Record<DragState, string | undefined> = {
  [DragState.Remove]: 'Remove',
  [DragState.MoveToEditor]: 'Move here',
  [DragState.None]: undefined,
};

interface DragPreviewProps {
  quill: QuillEditor;
}

const CarouselThumbnailDragPreview = ({ quill }: DragPreviewProps) => {
  const uploader = quill.uploader != null ? (quill.uploader as Uploader) : null;
  const activeDropTarget = uploader?.getActiveDropTarget();

  const [dragState, setDragState] = useState<DragState>(DragState.None);

  const [, connectBody] = useDrop<
    ThumbnailDragItem,
    { removed: boolean },
    unknown
  >({
    accept: CAROUSEL_ITEM_DRAG_TYPE,
    drop(item) {
      if (dragState !== DragState.None) {
        item.remove();
        return { removed: true };
      }
      return { removed: false };
    },
    hover(_item, monitor) {
      if (monitor.isOver({ shallow: true })) {
        setDragState(DragState.Remove);
      }
    },
  });
  connectBody(document.body);

  const [, connectQuillRoot] = useDrop<
    ThumbnailDragItem,
    { removed: boolean },
    unknown
  >({
    accept: CAROUSEL_ITEM_DRAG_TYPE,
    drop(item) {
      if (!isUploadedCarouselItem(item.image)) {
        setDragState(DragState.Remove);
        return { removed: true };
      }

      if (activeDropTarget != null) {
        const range = {
          index: quill.getIndex(activeDropTarget) + activeDropTarget.length(),
          length: 0,
        };

        uploader?.insertEmbeds(range, [
          {
            name: 'image',
            value: [
              {
                source: item.image.source,
              },
            ],
          },
        ]);
        item.remove();
        return { removed: true };
      }
      return { removed: false };
    },
    hover(item, monitor) {
      if (monitor.isOver({ shallow: true })) {
        setDragState(
          activeDropTarget == null || !isUploadedCarouselItem(item.image)
            ? DragState.Remove
            : DragState.MoveToEditor,
        );
      } else {
        setDragState(DragState.None);
      }
    },
  });
  connectQuillRoot(quill.root);

  const { isDragging, item, itemType, currentOffset } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    }),
  );

  if (!isDragging || itemType !== CAROUSEL_ITEM_DRAG_TYPE) {
    return null;
  }

  const { x, y } = currentOffset || { x: 0, y: 0 };

  return createPortal(
    <div className={styles.previewContainer}>
      <div
        style={{
          transform: `translate(${x}px, ${y}px)`,
        }}
      >
        <Tooltip content={TOOLTIP_LABELS[dragState]} noPortal open>
          <CarouselThumbnailImage
            image={item.image}
            selected={item.isSelected}
          />
        </Tooltip>
      </div>
    </div>,
    document.body,
  );
};

export default CarouselThumbnailDragPreview;
